import { Injectable } from '@angular/core';
import { HttpFunctionsService } from '../http-services/http-functions.service';
import { BehaviorSubject, map, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SoundKitListModel } from 'src/app/models/sound-kit-list.model';
import { FinalFilterModel } from 'src/app/models/filter';
import { SoundListModel, WaveformResponse } from 'src/app/models/sound-details.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SoundKitService {
  soundBaseUrl: string = "sounds/";
  topSoundsUrl : string = "playlist/slug/topsoundkits"
  updateElementData: BehaviorSubject<any> = new BehaviorSubject<any>({});
  constructor(private httpMethods: HttpFunctionsService) { }

  /**
   * get generate sound random id
   * @returns
   */
  getSoundKitSerialNumber() {
    return this.httpMethods.postMethod(this.soundBaseUrl + 'serial-number', {});
  }

  /**
   * get generate sound random id
   * @returns
   */
  getParticularSoundKit(soundId: any) {
    return this.httpMethods.getMethod(this.soundBaseUrl + 'public/'+soundId);
  }
  /**
   * get generate sound random id
   * @returns
   */
  getParticularSoundKitMySounds(soundId: any) {
    return this.httpMethods.getMethod(this.soundBaseUrl + 'user-sounds/'+soundId);
  }

  /**
   * create sound kit data
   * @param soundData 
   * @returns 
   */
  createAndUpdateSoundKit(soundData: any, actionType: string  = ""): Observable<any> {
    let url = actionType ? 'update' : 'create';
    if (actionType) {
      return this.httpMethods.putMethod(this.soundBaseUrl + url, soundData).pipe(
        map((response: any) => {
        return response;
        })
      );
    } else {
      return this.httpMethods.postMethod(this.soundBaseUrl + url, soundData).pipe(
        map((response: any) => {
          return response;
        })
      );
    }
  }

  /**
   * Update sound kit data
   * @param soundData 
   * @returns 
   */
  updateSoundKit(soundData: any): Observable<any> {
    return this.httpMethods.putMethod(this.soundBaseUrl + 'update', soundData).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  /**
   * Get all sound kits
   * @returns 
   */
  getAllSoundKits(pageNumber: number, isShowPop: boolean = true): Observable<SoundListModel> {
    return this.httpMethods.getMethod(this.soundBaseUrl + `user-sounds?page=${pageNumber}`, isShowPop).pipe(
      map((response: any) => {
        let soundListResponse:any =  "";
        if (response?.data &&  response?.data.length) {
          soundListResponse  = {
            soundData: response.data,
            page: response.page, 
            limit: response.limit, 
            total: response.total,
          }
        }
        return soundListResponse;
      })
    );
  }

deleteORArchivedParticularSound(soundId: any,callingType: string, status: string) {
    let apiUrl = callingType ? '/update-status' : '/delete';
    if (!callingType) {
      return this.httpMethods.deleteMethod(this.soundBaseUrl + soundId + apiUrl);
    } else {
      let sendData = {
        id: soundId,
        status: status,
      };
      return this.httpMethods.putMethod(
        this.soundBaseUrl + apiUrl,
        sendData
      );
    }
  }

  /**
   * get current tab data
   * @param currentTab 
   * @param currentIndex 
   */
  getCurrentTabData(currentTab: Array<any>, currentIndex: string) {
    let foundData: any =  currentTab.find(itemData => itemData.id === currentIndex);
    if (foundData) {
      return foundData;
    }
    return null;
  }

  /**
   * Initialize filter data with default values
   * @returns FinalFilterModel
   */
  filterDataInit(): FinalFilterModel {
    let filterInitData: FinalFilterModel = {
      page: {
        offset: 0,
        limit: 20,
      },
    };
    return filterInitData;
  }

  /**
   * get Sound Kit List Data for authenticated users without search (GET endpoint)
   * @param data
   * @returns
   */
  getSoundKitList(data: FinalFilterModel, isShowPop: boolean = true): Observable<Array<SoundKitListModel>> {
      // Build query params from filter data
      const params = new URLSearchParams();
      
      // Pagination - using limit and page (not offset)
      if (data.page) {
        params.append('limit', data.page.limit.toString());
        params.append('page', Math.floor(data.page.offset / data.page.limit) + 1 + '');
      }
      
      // Genres filter
      if (data.filter && data.filter.genres && data.filter.genres.length > 0) {
        params.append('genres', data.filter.genres.join(','));
      }
      
      // BPM range filter
      if (data.filter && data.filter.tempo) {
        if (data.filter.tempo.min != null) {
          params.append('bpmMin', data.filter.tempo.min.toString());
        }
        if (data.filter.tempo.max != null) {
          params.append('bpmMax', data.filter.tempo.max.toString());
        }
      }
      
      // Tags filter
      if (data.filter && data.filter.moods && data.filter.moods.length > 0) {
        params.append('tags', data.filter.moods.join(','));
      }
      
      // Sort
      // Default to newest first (createdAt descending)
      params.append('sort', '-createdAt');
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      return this.httpMethods.getMethod(this.soundBaseUrl + 'user-sounds' + queryString, isShowPop).pipe(
        map(response => {
          let sendData: Array<SoundKitListModel> = [];
          if (response.data) {
            sendData = response.data.map((soundKit: SoundKitListModel) => {
              // Set UI state properties
              soundKit.isFav = soundKit.isFavorite || false;
              soundKit.isSelected = false;
              
              return soundKit;
            });
          }
          return sendData;
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
    }
  
  /**
   * Get Top sound kits for sound list page
   * @param 
   * @returns 
  */
  getTopsoundKitList(): Observable<Array<any>>{
      return this.httpMethods.getMethod(this.topSoundsUrl).pipe(
        map(response => {
          // get the top sound kit array 
          if (response.playlists &&  response.playlists.length > 0) {
            return response.playlists[0].soundKits || [];
          }
          return [];
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  /**
   * get Public Sound Kit List Data with search (POST endpoint)
   * Used for: guests (all cases) and authenticated users with search
   * @param data
   * @returns
   */
  getPublicSoundKitList(data: FinalFilterModel, isShowPop: boolean = true): Observable<Array<SoundKitListModel>> {
    return this.httpMethods.postMethod(this.soundBaseUrl + 'public', data, isShowPop).pipe(
      map(response => {
        let sendData: Array<SoundKitListModel> = [];
        if (response.data) {
          sendData = response.data.map((soundKit: SoundKitListModel) => {
            // Set UI state properties
            soundKit.isFav = soundKit.isFavorite || false;
            soundKit.isSelected = false;
            
            return soundKit;
          });
        }
        return sendData;
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * get Pro Kit List Data 
   * @param data 
   * @returns 
   */
   getProKitList(data: FinalFilterModel, isShowPop: boolean = true): Observable<Array<SoundKitListModel>> {
    // Merge incoming filter data with kitType filter
    let filterData: FinalFilterModel = {
      ...data,
      filter: {
        ...data.filter,
        kitType: 'kit22'
      }
    };

    // Use the public endpoint since we need to filter by kitType
    return this.getPublicSoundKitList(filterData, isShowPop);
  }
  /**
   * get Royalty Free Kits List Data 
   * @param data 
   * @returns 
   */
   getRoyaltyFreeKits(data: FinalFilterModel, isShowPop: boolean = true): Observable<Array<SoundKitListModel>> {
    // Merge incoming filter data with kitType filter
    let filterData: FinalFilterModel = {
      ...data,
      filter: {
        ...data.filter,
        kitType: 'royalityfree'
      }
    };

    // Use the public endpoint since we need to filter by kitType
    return this.getPublicSoundKitList(filterData, isShowPop);
  }

  setDropdownValue(listOfData: Array<any>, keyName: any) {
    let selectedValue: any = "";
    if (listOfData && keyName) {
      if (typeof(keyName) === "string") {
        let value: any = listOfData.find((item: any) => item.key === keyName);
        selectedValue = value;
      } else {
        if (Array.isArray(keyName)) {
          let selectedArray: Array<any> = [];
            keyName.map(keyObj => {
              let value: any = listOfData.find((item: any) => item.key === keyObj);
              if (value) {
                selectedArray.push(value);
              }
            });
          selectedValue = selectedArray;
        }
      }
    }
    return selectedValue;
  }

  getSoundStreamUrl(userID: any, soundSerialNumber:any) {
    // return this.httpMethods.getMethod(this.soundBaseUrl + 'getticket-sound?user='+userID+'&sample='+soundSerialNumber);
    return this.httpMethods.getMethodCustom(
      environment.amazonGetTicketUrlSound +'?user='+userID+'&sample='+soundSerialNumber, false
    );
  }

  getPreviewPublicUrl(soundId: any, index: number) {
     return this.httpMethods.getMethod(this.soundBaseUrl
      + `preview/${soundId}?index=${index}`,
      false
    );
  }

  getPreviewFileUrl(soundPath: string, index: number) {
    return this.httpMethods.getMethod(
      `play-sound?soundid=${soundPath}&SpreviewIndex${index}`,
      false
    );
  }

  /**
   * Get waveform data for a sound kit preview file
   * @param userId - User ID who owns the sound kit (format: 'user-{id}')
   * @param sampleId - Sound kit sample ID (format: 'sample-{serialNumber}')
   * @param index - Preview file index (1-based)
   * @returns Observable with waveform data
   */
  getWaveformData(userId: string, sampleId: string, index: number): Observable<WaveformResponse> {
    const url = `${environment.waveformApiUrl}?userId=${userId}&sampleId=${sampleId}&index=${index}`;
    return this.httpMethods.getMethodCustom(url, false).pipe(
      map((response: any) => ({
        file: response.file || '',
        length: response.length || 0,
        data: response.data || []
      })),
      catchError(error => {
        console.error('Failed to fetch waveform data:', error);
        return throwError(() => error);
      })
    );
  }

}
